import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { EDemembrementType } from "common/enums/ProductSubscribed/EDemembrementType";
import { ESubscriptionProductType } from "common/enums/ProductSubscribed/ESubscriptionProductType";
import ProductSubscribedResponseResource from "common/resources/ProductSubscribed/ProductSubscribedResponseResource";
import Button from "components/elements/Button";
import LineInformation, { ELineInFoFormatType } from "components/elements/LineInformation";
import NavTabComponent from "components/elements/NavTabComponent";
import Typography, { ITypo, ITypoColor } from "components/elements/Typography";
import I18n from "components/materials/I18n";
import PageTemplate from "components/PageTemplate";
import ModuleConfig from "configs/ModuleConfig";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProductSubscribedService from "services/ProductSubscribedService";
import { container } from "tsyringe";
import DateUtils from "utils/DateUtils";
import { getViagerRatio } from "utils/baremeViager";

import classes from "./classes.module.scss";
import TooltipElement from "components/elements/TooltipElement";
import { ESubFilesTypes } from "common/enums/Subscription/File/ESubFilesTypes";
import ProductSubscribedToFileResponseResource from "common/resources/ProductSubscribed/File/ProductSubscribedToFileResponseResource";
import StatusTracker from "../../materials/StatusTracker";
import ProductSubscribedSignedFilesRequestResource from "common/resources/ProductSubscribed/File/ProductSubscribedSignedFilesRequestResource";
import SignedDocumentsElement from "./SignedDocumentsElement";
import SubscriptionService from "services/SubscriptionService";

const productSubscribedService = container.resolve(ProductSubscribedService);
const subscriptionService = container.resolve(SubscriptionService);
const pages = container.resolve(ModuleConfig).get().modules.pages;

export default function SubscriptionInformation() {
	const navigate = useNavigate();
	const [productSubscribed, setProductSubscribed] = useState<ProductSubscribedResponseResource | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [hasEsignature, setHasEsignature] = useState<boolean>(false);
	const [productSubScribedToFiles, setProductSubScribedToFiles] = useState<ProductSubscribedToFileResponseResource[]>([]);
	const [signedDocuments, setSignedDocuments] = useState<ProductSubscribedSignedFilesRequestResource[]>([]);

	const { productSubscribedId } = useParams();

	useEffect(() => {
		if (!productSubscribedId) return;
		productSubscribedService.getSignedDocuments(productSubscribedId).then((signedDocuments) => setSignedDocuments(signedDocuments));
	}, [productSubscribedId]);

	useEffect(() => {
		setLoading(true);
		if (!productSubscribedId) return;
		productSubscribedService.getByUid(productSubscribedId).then((productSubscribed) => {
			setProductSubscribed(productSubscribed);
			setLoading(false);
		});
	}, [productSubscribedId]);

	useEffect(() => {
		if (!productSubscribed || loading) return;

		const files = productSubscribed.productSubscribedToFiles
			?.map((productSubscribedToFile) => {
				if (productSubscribedToFile.type !== ESubFilesTypes.SCPI) return null;
				productSubscribedToFile.file.filePath = productSubscribedService.buildFileUrlByFileId(productSubscribedToFile.file.id);
				return productSubscribedToFile;
			})
			.filter((file) => file !== null) as ProductSubscribedToFileResponseResource[];
		setProductSubScribedToFiles(files);
	}, [productSubscribed, loading]);

	useEffect(() => {
		if (!productSubscribed) return;
		setLoading(true);
		subscriptionService.hasOngoingSignature(productSubscribed.subscriptionId).then((hasESignature) => {
			setHasEsignature(hasESignature);
			setLoading(false);
		});
	}, [productSubscribed]);

	const dueDate = useMemo(
		() => DateUtils.getDueDate(productSubscribed?.validationDate, productSubscribed?.productSheet?.dueDays, productSubscribed?.productSheet?.dueMonths),
		[productSubscribed],
	);

	const payoutDate = useMemo(() => {
		let dateForPayout = dueDate;
		if (
			productSubscribed?.subscriptionProductType === ESubscriptionProductType.nue_propriete &&
			productSubscribed?.demembrementType === EDemembrementType.temporaire &&
			productSubscribed?.demembrementDuration &&
			dueDate
		) {
			dateForPayout = new Date(dueDate);
			dateForPayout.setFullYear(dateForPayout.getFullYear() + productSubscribed.demembrementDuration);
		}
		return DateUtils.getPayoutDate(dateForPayout, productSubscribed?.productSheet?.distributionFrequency);
	}, [dueDate, productSubscribed]);

	const demembrementEndDate = useMemo(() => {
		const type = productSubscribed?.subscriptionProductType;
		if (!type || type === ESubscriptionProductType.pleine_propriete) return null;
		if (productSubscribed?.demembrementType !== EDemembrementType.temporaire) return null;
		if (!productSubscribed?.demembrementDuration || !dueDate) return null;
		const endDate = new Date(dueDate);
		endDate.setFullYear(endDate.getFullYear() + productSubscribed.demembrementDuration);
		return endDate;
	}, [productSubscribed, dueDate]);

	const demembrementEstimatedValue = useMemo(() => {
		const type = productSubscribed?.subscriptionProductType;
		if (!type || type === ESubscriptionProductType.pleine_propriete) return null;
		const quantity = productSubscribed?.quantity;
		const withdrawalPrice = productSubscribed?.productSheet?.withdrawalPrice;
		if (!quantity || !withdrawalPrice) return null;

		let ratio: number | null = null;
		if (productSubscribed?.demembrementType === EDemembrementType.viager && productSubscribed?.viagerAge != null) {
			ratio = getViagerRatio(productSubscribed.viagerAge, type);
		} else {
			const key = productSubscribed?.productSheet?.demembrementKeys?.find((k) => k.duration === productSubscribed?.demembrementDuration);
			if (key) {
				ratio = type === ESubscriptionProductType.usufruit ? key.usufruitRatio : key.nueProprieteRatio;
			}
		}

		if (ratio === null) return null;
		return quantity * withdrawalPrice * ratio;
	}, [productSubscribed]);

	const detailsPath = useMemo(() => {
		if (productSubscribed?.legacyReadOnly) {
			if (productSubscribed.subscriber?.clientId) {
				return pages.Clients.props.pages.PhysicalPersonClients.props.pages.PhysicalClientDetails.props.pages.LegacyHistory.props.path.replace(
					":clientId",
					productSubscribed.subscriber.clientId,
				);
			}
			if (productSubscribed.corporationSubscriber?.corporationId) {
				return pages.Clients.props.pages.Corporations.props.pages.CorporationDetails.props.pages.LegacyHistory.props.path.replace(
					":corporationId",
					productSubscribed.corporationSubscriber.corporationId,
				);
			}
		}
		return pages.SubscriptionSummary.props.pages.Subscribers.props.path.replace(":productSubscribedId", productSubscribedId ?? "");
	}, [productSubscribed, productSubscribedId]);

	if (!productSubscribedId) return;

	let title = I18n.trslt(I18n.asset.pages.subscription_information.page_title, {
		productName: productSubscribed?.productSheet?.name ?? "-",
		civility: productSubscribed?.subscriber?.civility ?? "-",
		subscriberName: productSubscribed?.subscriber?.lastName ?? "-",
	});

	if (productSubscribed?.corporationSubscriber) {
		title = I18n.trslt(I18n.asset.pages.subscription_information.page_title_corporation, {
			productName: productSubscribed?.productSheet?.name ?? "-",
			corporationName: productSubscribed?.corporationSubscriber?.raisonSociale ?? "-",
		});
	}

	const navTabItems = productSubScribedToFiles.map((productSubScribedToFile) => {
		if (!productSubScribedToFile.label) return null;
		return {
			label: productSubScribedToFile.label,
			children: <object aria-label={productSubScribedToFile.label} data={productSubScribedToFile.file.filePath!} width={"100%"} height={"900px"} type="application/pdf" />,
		};
	});
	if (signedDocuments.length > 0) {
		navTabItems.unshift({
			label: I18n.asset.pages.product.signed_documents,
			children: <SignedDocumentsElement productSubscribedId={productSubscribedId} signedDocuments={signedDocuments} />,
		});
	}

	return (
		<PageTemplate
			tabTitle={title}
			rightElement={
				<Button onClick={() => navigate(detailsPath)}>
					{productSubscribed?.legacyReadOnly ? "Historique prestataire" : I18n.asset.common.see_detail}
				</Button>
			}
			backArrow={{ enabled: true }}>
			<div className={classes["root"]}>
				<div className={classes["main-title-container"]}>
					<Typography typo={ITypo.DISPLAY_MEDIUM} color={ITypoColor.WILD_SAND_950}>
						{title}
					</Typography>
					{hasEsignature && <StatusTracker productSubscribedId={productSubscribedId} />}
				</div>
				<section className={classes["info"]}>
					<Typography className={classes["title"]} typo={ITypo.H2} color={ITypoColor.WILD_SAND_950}>
						{I18n.asset.pages.subscription_information.title}
					</Typography>
					{productSubscribed?.subscriptionProductType && (
						<LineInformation
							label={I18n.asset.pages.subscription_information.subscription_type}
							value={I18n.asset.enums.ESubscriptionProductType[productSubscribed.subscriptionProductType]}
						/>
					)}
					{productSubscribed?.subscriptionProductType && productSubscribed.subscriptionProductType !== ESubscriptionProductType.pleine_propriete && (
						<LineInformation
							label={I18n.asset.pages.subscription_information.contrepartie}
							value={productSubscribed.contrepartieNom ?? I18n.asset.pages.subscription_information.contrepartie_sans}
						/>
					)}
					<LineInformation
						label={I18n.asset.pages.subscription_information.validation_date}
						value={productSubscribed?.validationDate}
						formatType={ELineInFoFormatType.DATE}
					/>
					<LineInformation label={I18n.asset.pages.subscription_information.due_date} value={dueDate} formatType={ELineInFoFormatType.DATE} />
					{demembrementEndDate && (
						<LineInformation
							label={I18n.asset.pages.subscription_information.demembrement_end_date}
							value={demembrementEndDate}
							formatType={ELineInFoFormatType.DATE}
						/>
					)}
					<LineInformation label={I18n.asset.pages.subscription_information.payout_date} value={payoutDate} />
					<LineInformation label={I18n.asset.pages.subscription_information.share_number} value={productSubscribed?.quantity} formatType={ELineInFoFormatType.PART} />
					<LineInformation
						label={I18n.asset.pages.subscription_information.amount_subscribed}
						value={productSubscribed?.amountAtSubscription}
						formatType={ELineInFoFormatType.EURO}
					/>
					<LineInformation
						label={I18n.asset.pages.subscription_information.estimation_amount}
						value={productSubscribed?.productSheet?.withdrawalPrice && productSubscribed.quantity * productSubscribed.productSheet.withdrawalPrice}
						formatType={ELineInFoFormatType.EURO}
						icon={
							<TooltipElement title={I18n.asset.pages.subscription_information.estimation_amount_info}>
								<InformationCircleIcon height={24} width={24} style={{ cursor: "help" }} />
							</TooltipElement>
						}
					/>
					{demembrementEstimatedValue !== null && (
						<LineInformation
							label={I18n.asset.pages.subscription_information.estimation_amount_demembrement}
							value={demembrementEstimatedValue}
							formatType={ELineInFoFormatType.EURO}
						/>
					)}
				</section>

				{/* TODO get the files */}
				<NavTabComponent items={navTabItems} />
			</div>
		</PageTemplate>
	);
}
